import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import * as moment from 'moment';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteDashboardService } from '../../site-dashboard.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';
import { Subscription } from 'rxjs';
import { CommonDataService } from 'src/app/shared/services/common-data.service';

@Component({
	selector: 'app-monthly-quarterly-charger-usage-charts',
	templateUrl: './monthly-quarterly-charger-usage-charts.component.html',
	styleUrls: ['./monthly-quarterly-charger-usage-charts.component.css']
})
export class MonthlyQuarterlyChargerUsageChartsComponent implements OnInit, OnDestroy {
	@Input() device: any;
	@Input() getMaxKw: boolean = false;
	@Input() currentSite: any = {};
	siteId = 0;

	allYearsId = 9999;
	yearList: any[] = [];
	selectedYear: number = new Date().getFullYear();
	siteInstallationDate = null;
	noData = false;

	selectedPeriodType = 'monthly';
	selectedCharts = 'all';

	chartsData = {
		total_charge_hours: {},
		total_charge_ah: {},
		total_charge_kwh: {},
		total_max_kw: {},
	};

	monthlyData = {};
	quarterlyData = {};

	chartObject = {
		config:  {
			legend: 'none',
			bar: {
				groupWidth: 30
			},
			tooltip: {isHtml: true},
		},
		columnNames: ['Time', 'Data'],
		hasData: false
	}

	timeChartConfig: any = {...this.chartObject.config};
	timeColumnNames: any = [...this.chartObject.columnNames, { role: 'tooltip', type: 'string', p: {html: false}}];

	periodTypes = [
		{label: this.translate.instant('g.quarterly'), id: 'quarterly'},
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.daily'), id: 'daily'},
	];

	chartsList = [
		{id: 'all', label: this.translate.instant('g.all')},
		{id: 'charge_hours', label: this.translate.instant('fleet_reports.charge_hours')},
		{id: 'charge_ah', label: this.translate.instant('fleet_reports.charge_ah')},
		{id: 'charge_kwh', label: this.translate.instant('fleet_reports.charge_kwh')},
	];

	siteSubscription: Subscription = new Subscription();

	get printFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'g.year', value: this.commonService.getOptionLabel(this.yearList, this.selectedYear) }
		];

		if (!this.isAllYearsSelected())
			filters.push({ label: 'perf_analytic.period', value: this.commonService.getOptionLabel(this.periodTypes, this.selectedPeriodType) });

		filters.push({ label: 'g.charts', value: this.commonService.getOptionLabel(this.chartsList, this.selectedCharts) });

		return filters;
	}

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
		private commonDataService: CommonDataService
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		if (this.device) {
			if (this.device?.config_info?.installation_date)
				this.siteInstallationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

			this.getChargerUsageChart();
			const {yearList, currentYear} = this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
			this.yearList = yearList;
			this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
			this.selectedYear = currentYear;
		} else {
			this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
				if (siteInfo && siteInfo.installation_date) {
					this.currentSite = siteInfo;
					this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
					this.getChargerUsageChart();
					const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
					this.yearList = yearList;
					this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
					this.selectedYear = currentYear;
				}
			});
		}

		if (this.getMaxKw) {
			this.chartsList.splice(3, 0, {
				id: 'max_kw',
				label: this.translate.instant('fleet_reports.max_kw'),
			});
		}
		this.checkSelectedYearData();
	}

	ngOnChanges(changes){
		if(changes.device && this.device && this.siteId)
			this.getChargerUsageChart();
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	selectYear() {
		if (this.selectedYear === this.allYearsId) {
			let data = this.quarterlyData;
			if (this.selectedPeriodType === 'monthly')
				data = this.monthlyData;

			this.prepareQuarterlyMonthlyData(data);
			this.checkSelectedYearData();
			return;
		}

		if (this.selectedPeriodType === 'daily')
			return this.getChargerUsageChart();

		this.checkSelectedYearData();
	}

	changePeriodType() {
		if (this.selectedPeriodType === 'daily') {
			this.noData = false;
			return this.getChargerUsageChart();
		}

		if (this.selectedPeriodType === 'monthly')
			return this.prepareQuarterlyMonthlyData(this.monthlyData);

		return this.prepareQuarterlyMonthlyData(this.quarterlyData);
	}

	getChargerUsageChart() {
		const options = {
			is_daily: this.selectedPeriodType === 'daily',
			year: this.selectedYear,
			site_id: this.siteId,
			mac_address: this.device?.mac_address,
			sct_devices_types: [DeviceFactory.types.charglink, DeviceFactory.types.brm],
			get_max_kw: this.getMaxKw,
		};

		this.sitesService.getUsageChart(this.siteId, options).subscribe((res: any) => {
			if (this.selectedPeriodType === 'daily')
				this.prepareDailyData(res);
			else {
				this.monthlyData = res.monthly;
				this.quarterlyData = res.quarterly;

				if (this.selectedPeriodType === 'monthly') {
					this.prepareQuarterlyMonthlyData(this.monthlyData);
				} else {
					this.prepareQuarterlyMonthlyData(this.quarterlyData);
				}
			}
		});
	}

	exportDataToCsv() {
		const csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device ? this.device.serial_number : null
		});

		let csvData = [...csvFileHeaderInfo];
		for (let key in this.chartsData) {
			if (this.selectedCharts === 'charge_hours' &&  key.indexOf('hours') === -1)
				continue;

			if (this.selectedCharts === 'charge_ah' &&  key.indexOf('ah') === -1)
				continue;

			if (this.selectedCharts === 'charge_kwh' &&  key.indexOf('kwh') === -1)
				continue;

			if (this.selectedCharts === 'max_kw' &&  key.indexOf('max_kw') === -1)
				continue;

			csvData.push([this.translate.instant('fleet_reports.' + key)]);
			csvData.push([this.translate.instant("g.time"), this.translate.instant("g.data")]);

			const chartData = [...this.chartsData[key][this.selectedYear]];
			chartData.forEach((item) => {
				if (item.length > 2)
					item.pop();

				if (item[0] instanceof Date)
					item[0] = moment(item[0]).format("MM/DD/YYYY");
			});

			csvData.push(...chartData);
			csvData.push([]);
		}

		const selectedPeriodTypeLabel = this.periodTypes.find((period) => period.id === this.selectedPeriodType)?.label || this.translate.instant('g.monthly');
		new AngularCsv(csvData, `${selectedPeriodTypeLabel}_${this.translate.instant('fleet_reports.charger_usage_charts')}`);
	}

	prepareDailyData(data) {
		this.resetCharts();
		const yearId = this.selectedYear;
		this.chartsData.total_charge_hours[yearId] = [];
		this.chartsData.total_charge_ah[yearId] = [];
		this.chartsData.total_max_kw[yearId] = [];
		this.chartsData.total_charge_kwh[yearId] = [];
		this.chartObject.columnNames = [this.translate.instant("g.time"), this.translate.instant("g.date")];

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		for (const day in data) {
			const date = data[day].day;
			const totalChargeSeconds = data[day].total_charge_second ? +(data[day].total_charge_second).toFixed(0) : 0;
			const totalChargeAh = data[day].total_as ? +(data[day].total_as / 3600).toFixed(0) : 0;
			const totalMaxKw = data[day].max_kw ? +(data[day].max_kw).toFixed(2) :0;
			const totalChargeKwh = data[day].total_ws ? +(data[day].total_ws / (1000 * 3600)).toFixed(0) : 0;

			const [year, month, dayString] = date.split('-').map(Number);

			this.chartsData.total_charge_hours[yearId].push([new Date(year, month - 1, dayString), totalChargeSeconds / 3600 || 0, this.commonService.formatPopupTime(totalChargeSeconds || 0, new Date(date))]);
			this.chartsData.total_charge_ah[yearId].push([new Date(year, month - 1, dayString), totalChargeAh || 0]);
			this.chartsData.total_max_kw[yearId].push([new Date(year, month - 1, dayString), totalMaxKw || 0]);
			this.chartsData.total_charge_kwh[yearId].push([new Date(year, month - 1, dayString), totalChargeKwh || 0]);
		}
	}

	prepareQuarterlyMonthlyData(data) {
		this.resetCharts();

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		const emptyData = this.getEmptyData();

		for (let year of this.yearList) {
			const yearId = year.id;
			const totalChargeSeconds = data?.data_total_charge_duration_seconds?.[yearId] || emptyData;
			const totalChargeAh = data.data_total_ahr?.[yearId] || emptyData;
			const totalMaxKw = data.data_total_max_kw?.[yearId]  || emptyData;
			const totalChargeKwh = data.data_total_kwhr?.[yearId] || emptyData;

			const noAnalyticData = !Object.keys(data).some((key) => {
				return data[key][yearId] && data[key][yearId].length > 0;
			});

			if (noAnalyticData)
				continue;

			this.noData = false;
			this.chartsData.total_charge_hours[yearId] = [];
			this.chartsData.total_charge_ah[yearId] = [];
			this.chartsData.total_charge_kwh[yearId] = [];
			this.chartsData.total_max_kw[yearId] = [];

			this.chartsData.total_charge_hours[this.allYearsId] ||= [];
			this.chartsData.total_charge_ah[this.allYearsId] ||= [];
			this.chartsData.total_charge_kwh[this.allYearsId] ||= [];
			this.chartsData.total_max_kw[this.allYearsId] ||= [];

			let yearlyTotalChargeSeconds = 0;
			let yearlyTotalChargeAh = 0;
			let yearlyTotalChargeKwh = 0;
			let yearlyTotalMaxKw = 0;

			for (const period of totalChargeSeconds) {
				yearlyTotalChargeSeconds += period[1];
				const hours = Math.round(period[1] / 3600);
				this.chartsData.total_charge_hours[yearId].push([period[0], hours || 0, this.commonService.formatPopupTime(period[1] || 0, period[0])]);
			}

			for (const period of totalChargeAh) {
				yearlyTotalChargeAh += period[1];
				this.chartsData.total_charge_ah[yearId].push([period[0], +(period[1] || 0).toFixed(0)]);
			}

			for (const period of totalChargeKwh) {
				yearlyTotalChargeKwh += period[1];
				this.chartsData.total_charge_kwh[yearId].push([period[0], +(period[1] || 0).toFixed(0)]);
			}

			
			for (const period of totalMaxKw) {
				yearlyTotalMaxKw = Math.max(yearlyTotalMaxKw, period[1]);
				this.chartsData.total_max_kw[yearId].push([period[0], +(period[1] || 0).toFixed(2)]);
			}
	
			this.chartsData.total_charge_hours[this.allYearsId].push([String(yearId), Math.round(yearlyTotalChargeSeconds / 3600) || 0, this.commonService.formatPopupTime(yearlyTotalChargeSeconds || 0, yearId)]);
			this.chartsData.total_charge_ah[this.allYearsId].push([String(yearId), +(yearlyTotalChargeAh || 0).toFixed(0)]);
			this.chartsData.total_charge_kwh[this.allYearsId].push([String(yearId), +(yearlyTotalChargeKwh || 0).toFixed(0)]);
			this.chartsData.total_max_kw[this.allYearsId].push([String(yearId), +(yearlyTotalMaxKw || 0).toFixed(2)]);
		}

		this.chartsData.total_charge_hours[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.total_charge_ah[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.total_charge_kwh[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.total_max_kw[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));

		this.checkSelectedYearData();
	}

	getEmptyData() {
		const emptyData = [];

		if (this.selectedPeriodType === 'monthly') {
			for (let i = 0; i < 12; i++) {
				emptyData.push([this.commonDataService.shortMonths[i], 0]);
			}
		} else if (this.selectedPeriodType === 'quarterly') {
			for (let i = 1; i <= 4; i++) {
				emptyData.push([`Q${i}`, 0]);
			}
		}

		return emptyData;
	}

	calculateTicksForTimeCharts() {
		let keys = ['total_charge_hours'];

		const data = this.chartsData;
		let max = 0;

		for(const key of keys) {
			if(data[key][this.selectedYear]) {
				for(const item of data[key][this.selectedYear]) {
					let sum = 0;
					for(let i = 1; i < item.length; i += 2) {
						sum += item[i];
					}

					if(sum > max)
						max = sum;
				}
			}
		}

		let ticks = [];
		let interval = 300;
		let maxInterval = (Math.ceil(max / interval) * interval) * 1.1;

		if (maxInterval < 300)
			maxInterval = 300; // minimum interval of 5 minutes

		if(maxInterval)
			interval = maxInterval / 5; // 5 steps only

		for(let i = 0; i <= maxInterval; i += interval) {
			let hours = Math.floor(i / 3600);
			let minutes = Math.floor((i % 3600) / 60);
			let seconds = i % 60;
			let time = (maxInterval > 3600 ? hours + ':' : '') + (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds.toFixed(0);
			ticks.push({v: i, f: time});
		}

		return {ticks, title: `(${maxInterval > 3600 ? 'H:' : ''}M:S)`};
	};

	resetCharts() {
		this.chartsData = {
			total_charge_hours: {},
			total_charge_ah: {},
			total_charge_kwh: {},
			total_max_kw: {},
		};
	}

	checkSelectedYearData() {
		this.noData = false;
		if (
			!this.chartsData.total_charge_hours[this.selectedYear]?.length &&
			!this.chartsData.total_charge_ah[this.selectedYear]?.length &&
			!this.chartsData.total_charge_kwh[this.selectedYear]?.length &&
			!this.chartsData.total_max_kw[this.selectedYear]?.length
		) {
			return this.noData = true;
		}
	}

	isChartHaveZoom() {
		return this.selectedPeriodType === 'daily';
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
